import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardBody, Container, Row, Col,
  Input, Button, InputGroup, InputGroupAddon
} from "reactstrap";
import api from "../../services/api";
import { getToken, isConsulting, getUserCustomer, canAccessPermissions } from "../../services/auth";
import PlayYouTubeVideo from "../../components/Modals/PlayYouTubeVideo.js";
import { getYouTubeVideoUrl } from "../../utils/youtubeCitations.js";

const DEFAULT_MESSAGES = [
  { role: 'assistant', content: 'Olá! Sou o <b>SmartChat</b>. Como posso ajudar com os processos SAP hoje?' }
];

const CLIENTE_ID_STORAGE_KEY = 'smartflow_google_chat_cliente_id';

const GoogleChatPage = () => {
  const [messages, setMessages] = useState(() => {
    const savedChat = localStorage.getItem('smartflow_google_chat_history');
    if (!savedChat) return DEFAULT_MESSAGES;

    try {
      const parsedChat = JSON.parse(savedChat);
      return Array.isArray(parsedChat) ? parsedChat : DEFAULT_MESSAGES;
    } catch (error) {
      localStorage.removeItem('smartflow_google_chat_history');
      return DEFAULT_MESSAGES;
    }
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoModalOpened, setVideoModalOpened] = useState(false);
  const [videoModalSource, setVideoModalSource] = useState('');
  const [videoModalTitle, setVideoModalTitle] = useState('');

  // O seletor de cliente é ferramenta de teste: só aparece pra perfil admin
  // (canManagePermissions). Usuário do lado do cliente (1 cliente só) tem o
  // filtro aplicado automaticamente, sem mostrar nada na tela. Os demais
  // perguntam sem filtro (base inteira), igual a antes dessa mudança.
  const canPickCliente = canAccessPermissions();
  const fixedCustomer = !canPickCliente && !isConsulting() ? getUserCustomer() : null;
  const [customers, setCustomers] = useState([]);
  const [clienteId, setClienteId] = useState(() => {
    if (fixedCustomer) return fixedCustomer.id;
    if (!canPickCliente) return '';
    return localStorage.getItem(CLIENTE_ID_STORAGE_KEY) || '';
  });

  useEffect(() => {
    if (!canPickCliente) return;
    api.get('customer/all').then(response => setCustomers(response.data)).catch(() => {});
  }, [canPickCliente]);

  useEffect(() => {
    if (!canPickCliente) return;
    if (clienteId) {
      localStorage.setItem(CLIENTE_ID_STORAGE_KEY, clienteId);
    } else {
      localStorage.removeItem(CLIENTE_ID_STORAGE_KEY);
    }
  }, [clienteId, canPickCliente]);

  const messagesEndRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const messagesRef = useRef(messages);
  const abortControllerRef = useRef(null);
  const prevLoadingRef = useRef(loading);

  const handleMessageClick = (e) => {
    const link = e.target.closest('a');
    const videoUrl = link && getYouTubeVideoUrl(link.getAttribute('href'));
    if (!videoUrl) return;

    e.preventDefault();

    const citation = link.closest('.citation-mark');
    setVideoModalTitle(citation?.getAttribute('data-tooltip') || 'Vídeo');
    setVideoModalSource(videoUrl);
    setVideoModalOpened(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpened(false);
    setVideoModalSource('');
    setVideoModalTitle('');
  };

  useEffect(() => {
    messagesRef.current = messages;
    localStorage.setItem('smartflow_google_chat_history', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      requestAnimationFrame(() => {
        if (lastUserMessageRef.current) {
          lastUserMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const clearChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    messagesRef.current = DEFAULT_MESSAGES;
    setMessages(DEFAULT_MESSAGES);
    setInput('');
    setLoading(false);
    localStorage.setItem('smartflow_google_chat_history', JSON.stringify(DEFAULT_MESSAGES));
  };

  const handleSend = async (overrideQuestion) => {
    const questionToSend = overrideQuestion ?? input;
    if (!questionToSend.trim() || loading) return;

    const userMsg = { role: 'user', content: questionToSend };
    const currentMessages = messagesRef.current;
    const updatedMessages = [...currentMessages, userMsg];
    const currentQuestion = questionToSend;
    const historyToSend = currentMessages
      .filter(message =>
        message &&
        message.content &&
        !(message.role === 'assistant' && message.content.includes('Sou o <b>SmartChat</b>'))
      )
      .slice(-10);
    const abortController = new AbortController();

    abortControllerRef.current = abortController;

    setMessages([...updatedMessages, { role: 'assistant', content: '', source: null }]);
    setInput('');
    setLoading(true);

    try {
      const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || '';
      const token = getToken();
      const response = await fetch(`${baseURL}/google-chat/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question: currentQuestion,
          history: historyToSend,
          ...(clienteId ? { clienteId } : {}),
        }),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error('Falha na conexão com o servidor.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedText = '';
      let source = null;
      let suggestions = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const raw = part.slice(6).trim();
          if (raw === '[DONE]') continue;

          try {
            const parsed = JSON.parse(raw);
            if (parsed.error) {
              accumulatedText = parsed.error;
            } else if (parsed.source !== undefined) {
              source = parsed.source;
            } else if (parsed.text) {
              accumulatedText += parsed.text;
            }

            if (Array.isArray(parsed.suggestions) && parsed.suggestions.length > 0) {
              suggestions = parsed.suggestions;
            }

            const nextContent = accumulatedText;
            const nextSource = source;
            const nextSuggestions = suggestions;

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: nextContent,
                source: nextSource,
                suggestions: nextSuggestions,
              };
              return updated;
            });
          } catch (e) {
            continue;
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Erro ao conectar ao servidor do SmartFlow.',
        };
        return updated;
      });
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      setLoading(false);
    }
  };

  return (
    <>
      <div className="header bg-info pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="6" xs="7">
                <h6 className="h2 text-white d-inline-block mb-0">SmartChat</h6>
              </Col>
              <Col className="text-right" lg="6" xs="5">
                <Button className="btn-neutral" color="default" onClick={clearChat} size="sm">
                  Limpar Conversa
                </Button>
              </Col>
            </Row>
            {canPickCliente && (
              <Row className="align-items-center pb-3">
                <Col xs="12" className="d-flex align-items-center" style={{ gap: "10px" }}>
                  <span className="text-white small">Cliente:</span>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    style={{ maxWidth: "260px" }}
                  >
                    <option value="">Todos (base global)</option>
                    {customers.map(customer => (
                      <option key={customer._id} value={customer._id}>
                        {customer.shortName || customer.fullName}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            )}
          </div>
        </Container>
      </div>

      <Container className="mt--6" fluid>
        <Row>
          <Col md="12">
            <Card className="shadow border-0" style={{ height: "75vh", display: "flex", flexDirection: "column" }}>
              <CardBody
                style={{ flex: 1, overflowY: "auto", padding: "1.5rem", background: "#f6f9fc" }}
                onClick={handleMessageClick}
              >
                {(() => {
                  let lastUserIndex = -1;
                  for (let idx = messages.length - 1; idx >= 0; idx--) {
                    if (messages[idx].role === 'user') {
                      lastUserIndex = idx;
                      break;
                    }
                  }
                  return messages.map((msg, i) => (
                  <div key={i} className="mb-4" style={{ scrollMarginTop: "16px" }} ref={i === lastUserIndex ? lastUserMessageRef : null}>
                    <div className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                      <div className={`p-3 shadow-sm ${msg.role === 'user' ? 'bg-info text-white' : 'bg-white text-dark'}`}
                           style={{ borderRadius: "15px", maxWidth: "80%" }}>
                        <div
                          className="smart-chat-message-content"
                          dangerouslySetInnerHTML={{ __html: msg.content }}
                        />
                      </div>
                    </div>

                    {msg.role === 'assistant' && !loading && i === messages.length - 1 &&
                      Array.isArray(msg.suggestions) && msg.suggestions.length > 0 && (
                      <div className="d-flex flex-wrap mt-2" style={{ gap: "8px" }}>
                        {msg.suggestions.map((suggestion, si) => (
                          <button
                            key={si}
                            type="button"
                            onClick={() => handleSend(suggestion)}
                            disabled={loading}
                            className="btn btn-sm"
                            style={{
                              borderRadius: "16px",
                              border: "1px solid #11cdef",
                              background: "#fff",
                              color: "#11cdef",
                              fontSize: "0.8rem",
                              padding: "6px 14px",
                            }}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  ));
                })()}
                {loading && <div className="text-muted small mb-3">
                  <i className="fa fa-circle-notch fa-spin mr-2"></i>Analisando base de conhecimento...
                </div>}
                <div ref={messagesEndRef} />
              </CardBody>

              <div className="p-4 border-top bg-white">
                <InputGroup className="input-group-merge shadow-none border-1">
                  <Input
                    placeholder="Sua dúvida técnica..."
                    type="text"
                    value={input}
                    style={{ border: "1px solid #dee2e6", borderRadius: "25px 0 0 25px", paddingLeft: "20px" }}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      color="info"
                      onClick={() => handleSend()}
                      disabled={loading}
                      style={{ borderRadius: "0 25px 25px 0", padding: "0 25px" }}
                    >
                      {loading ? '...' : 'Enviar'}
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
      <PlayYouTubeVideo
        title={videoModalTitle}
        modalOpened={videoModalOpened}
        modalClose={closeVideoModal}
        source={videoModalSource}
      />
    </>
  );
};

export default GoogleChatPage;
